import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { compose } from "recompose";
import { withStyles, Button, Dialog, DialogTitle, DialogContent, DialogActions } from "@material-ui/core";
import "leaflet-draw/dist/leaflet.draw.css";
import Leaflet from "leaflet";
import { FeatureGroup, Marker } from "react-leaflet";
import { EditControl } from "react-leaflet-draw";
import t from "../../../../../languages/pl";
import MapComponent from "../../../../MapComponents/MapComponent";
import { defaultCenter } from "../../../../MapComponents/MapComponent";
import CancelAcceptForm from "../../../../Common/CancelAcceptForm";
import { buttonStyle } from "../buttonGroupStyle";
import AssignElementsDialog from "../../../../Common/AssignCarsDialog";
import withSubordinateUser from './../../../../HOC/withSubordinateUser';
import CarMapIcon from "../../../../MapComponents/Map/CarMapIcon";
import { notArrayOrEmpty } from "../../../../../utils/helpers";
import {connect} from "react-redux";

const useStyles = theme => ({
    button: buttonStyle(theme),
    dialogActions: {
        margin: theme.spacing(2)
    },
});

const setUpEditControl = () => {
    Leaflet.drawLocal.draw.toolbar.buttons.circle = t.drawZoneOnMap;
    Leaflet.drawLocal.draw.toolbar.actions.title = t.cancelDrawing;
    Leaflet.drawLocal.draw.toolbar.actions.text = t.cancel;
    Leaflet.drawLocal.draw.handlers.circle.tooltip.start = t.pickMapPointAndDraw;
    Leaflet.drawLocal.draw.handlers.simpleshape.tooltip.end = t.releaseMouseButton;
    Leaflet.drawLocal.draw.handlers.circle.radius = t.radius;
    Leaflet.drawLocal.edit.toolbar.actions.save.title = t.saveChanges;
    Leaflet.drawLocal.edit.toolbar.actions.save.text = t.save;
    Leaflet.drawLocal.edit.toolbar.actions.cancel.title = t.undoChanges;
    Leaflet.drawLocal.edit.toolbar.actions.cancel.text = t.cancel;
    Leaflet.drawLocal.edit.toolbar.actions.clearAll.title = t.clearAllZones;
    Leaflet.drawLocal.edit.toolbar.actions.clearAll.text = t.clear;
    Leaflet.drawLocal.edit.toolbar.buttons.edit = t.editZones;
    Leaflet.drawLocal.edit.toolbar.buttons.remove = t.deleteZones;
    Leaflet.drawLocal.edit.handlers.edit.tooltip.text = t.clickAndDragToEdit;
    Leaflet.drawLocal.edit.handlers.edit.tooltip.subtext = t.clickCancelToUndo;
    Leaflet.drawLocal.edit.handlers.remove.tooltip.text = t.clickZoneToDelete;
};

const checkCoordsExist = (coords) => coords && Object.keys(coords).includes('lat') && Object.keys(coords).includes('lon');

const defaultZoom = 6;

const Zoning = props => {
    const { classes, buttonTitle, title, name, dialogOpenCallback, zones: inputZones,
        onSubmit, onCancel, onDefaultZone, nonAdminUser, carCoords, carType, isGpsEnabled } = props;
    const [open, setOpen] = useState(false);
    const [assignDialog, setAssignDialog] = useState(false);
    const [assignIds, setAssignIds] = useState([]);
    const map = useRef(null);
    const featureGroup = useRef(null);
    const [controlSetUp, setControl] = useState(false);
    const [zones, setZones] = useState([]);

    useEffect(() => {
        if(!map.current)
            return;
        if (open) {
            setUpEditControl();
            map.current.leafletElement.setView(defaultCenter, defaultZoom);
            setControl(true);
        }
        else {
            setControl(false);
            setZones([]);
            setAssignIds([]);
        }
    }, [open]);

    useEffect(() => {
        let group = featureGroup.current;
        let mapRef = map.current;
        if (!group || !mapRef)
            return;
        let initialZones = initialSetUp();
        if (!notArrayOrEmpty(initialZones)) {
            let groupBounds = group.leafletElement.getBounds();
            if(checkCoordsExist(carCoords))
                groupBounds.extend(carCoords);
            mapRef.leafletElement.fitBounds(groupBounds, { maxZoom: defaultZoom });
        }
        else if(checkCoordsExist(carCoords)) {
            mapRef.leafletElement.setView(carCoords, defaultZoom);
        }
    }, [controlSetUp, inputZones, carCoords]);

    const initialSetUp = () => {
        let group = featureGroup.current.leafletElement;
        group.clearLayers();
        let initialZones;
        if (Array.isArray(inputZones) && inputZones.length > 0)
            initialZones = inputZones;
        if (initialZones && zones !== initialZones) {
            let leafletZones = initialZones.map(zone => new Leaflet.Circle([zone.lat, zone.lon], { radius: zone.rad, opacity: 0.7 }));
            leafletZones.forEach(zone => {
                zone.bindTooltip(`${t.radius} ${(zone.options.radius / 1000).toFixed(2)} km`, { direction: "top", sticky: true });
                group.addLayer(zone);
            });
            setZones(leafletZones);
            return leafletZones;
        }
    };

    const handleOpenDialogClick = () => {
        setOpen(true);
        if (dialogOpenCallback)
            dialogOpenCallback();
    };

    const handleZoneSubmit = () => {
        setOpen(false);
        let dts = zones.map(zone => {
            let center = zone.getLatLng();
            let radius = zone.getRadius();
            return {
                lat: center.lat,
                lon: center.lng,
                rad: radius
            };
        });
        if (onSubmit)
            onSubmit(dts, assignIds);
    };

    const handleCarAssignSubmit = (carIds) => {
        setAssignIds(carIds);
        setAssignDialog(false);
    };

    const handleDefaultZoneClick = () => {
        let group = featureGroup.current.leafletElement;
        group.clearLayers();
        if (onDefaultZone)
            onDefaultZone();
    };

    const handleZonesCreated = (e) => {
        setZones(zones => [...zones, e.layer]);
    };

    const handleZonesEdited = (e) => {
        Object.values(e.layers._layers).forEach(layer => {
            layer.closeTooltip();
        });
        let editedLayers = e.layers._layers;
        setZones(zones => editZones(zones, editedLayers));
    };

    const handleCircleResizing = (e) => {
        e.layer.setTooltipContent(`${t.radius} ${(e.layer._mRadius / 1000).toFixed(2)} km`);
        e.layer.openTooltip();
    };

    const handleZonesDeleted = (e) => {
        let deletedLayers = e.layers._layers;
        setZones(zones => deleteZones(zones, deletedLayers));
    };

    const handleCancel = () => {
        setOpen(false);
        if (onCancel)
            onCancel();
    };

    const editZones = (zones, editedZones) => {
        let idsToEdit = Object.keys(editedZones);
        let newZones = zones.map(zone => {
            let idToEdit = idsToEdit.find(id => id === zone._leaflet_id.toString());
            if (!idToEdit)
                return zone;
            else return editedZones[idToEdit];
        });
        return newZones;
    };

    const deleteZones = (zones, zoneToDelete) => {
        let idsToEdit = Object.keys(zoneToDelete);
        let newZones = zones.filter(zone => {
            let idToEdit = idsToEdit.find(id => id === zone._leaflet_id.toString());
            if (!idToEdit)
                return zone;
            return false;
        });
        return newZones;
    };

    return (
        <React.Fragment>
            <Button
                variant="outlined"
                className={classes.button}
                disabled={!isGpsEnabled}
                onClick={handleOpenDialogClick}
            >
                {buttonTitle}
            </Button>
            <Dialog
                open={open}
                keepMounted
                fullWidth={true}
                maxWidth="xl"
                onClose={() => setOpen(false)}
            >
                <DialogTitle>
                    {title}&nbsp;{name}
                </DialogTitle>
                <DialogContent>
                    <MapComponent ref={map} style={{ height: "70vh" }}>

                        {
                            checkCoordsExist(carCoords) &&
                            <Marker
                                icon={CarMapIcon(carType, name)}
                                position={carCoords}
                            />
                        }
                        {/* //first drawLocal needs to be setup for correct tooltips on EditControl */}
                        {controlSetUp &&
                            <FeatureGroup ref={featureGroup}>
                                <EditControl
                                    position="topleft"
                                    draw={{
                                        rectangle: false,
                                        polygon: false,
                                        marker: false,
                                        circlemarker: false,
                                        polyline: false,
                                    }}
                                    onCreated={handleZonesCreated}
                                    onEditResize={handleCircleResizing}
                                    onEdited={handleZonesEdited}
                                    onDeleted={handleZonesDeleted}
                                />
                            </FeatureGroup>
                        }
                    </MapComponent>
                </DialogContent>
                <DialogActions className={classes.dialogActions}>
                    <Button
                        variant="contained"
                        onClick={handleDefaultZoneClick}
                    >
                        {t.defaultZonesPoland}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => setAssignDialog(true)}
                    >
                        {t.assignToCarsInGroup}
                    </Button>
                    <CancelAcceptForm onSubmit={handleZoneSubmit} onCancel={handleCancel} disableSubmit={nonAdminUser} />
                </DialogActions>
            </Dialog>
            {
                assignDialog
                &&
                <AssignElementsDialog
                    open={assignDialog}
                    onClose={() => setAssignDialog(false)}
                    onSubmit={handleCarAssignSubmit}
                    title={t.toZone}
                />
            }
        </React.Fragment>
    );
};


const mapStateToProps = (state) => ({
    isGpsEnabled: state.authReducer.isGpsEnabled,
});
export default compose(
    connect(
        mapStateToProps,
        null
    ),
    withStyles(useStyles),
    withSubordinateUser
)(Zoning);

Zoning.propTypes = {
    classes: PropTypes.object,
    buttonTitle: PropTypes.string,
    title: PropTypes.string,
    name: PropTypes.string,
    dialogOpenCallback: PropTypes.func,
    zones: PropTypes.array,
    carCoords: PropTypes.object,
    carType: PropTypes.string,
    onDefaultZone: PropTypes.func,
    onSubmit: PropTypes.func,
    onCancel: PropTypes.func,
    //withSubordinateUser
    nonAdminUser: PropTypes.bool,
    isGpsEnabled: PropTypes.bool
};

Zoning.defaultProps = {
    buttonTitle: t.zoning,
    title: t.zoningTitle
};
